export const variantConstants = {
  H1: "h1",
  H2: "h2",
  H3: "h3",
  H4: "h4",
  PAGETITLE: "pageTitle",
  SECTIONTITLE: "sectionTitle",
  SUBTITLE: "subTitle",
  PARAGRAPHLEADING: "paragraphLeading",
  PARAGRAPHDEFAULT: "paragraphDefault",
  PARAGRAPHSMALL: "paragraphSmall",
  BUTTONLARGE: "buttonLarge",
  BUTTONDEFAULT: "buttonDefault",
  BUTTONSMALL: "buttonSmall",
  CAPTION: "caption",
  CAPTIONSMALL: "captionSmall",
  OVERLINE: "overline",
};

export const variantMappingConstants = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  pageTitle: "p",
  sectionTitle: "p",
  subTitle: "p",
  paragraphLeading: "p",
  paragraphDefault: "p",
  paragraphSmall: "p",
  buttonLarge: "p",
  buttonDefault: "p",
  buttonSmall: "p",
  caption: "span",
  captionSmall: "span",
  overline: "p",
};

const commonProperties = (...values: any) => {
  const [fontSize, fontWeight, lineHeight, letterSpacing] = values;
  return {
    fontSize,
    fontWeight,
    lineHeight: `${lineHeight}px`,
    letterSpacing,
    fontFamily: "Montserrat",
    margin: null,
    color: "black",
  };
};
declare module "@mui/material/styles" {
  interface TypographyVariants {
    pageTitle: React.CSSProperties;
    paragraphLeading: React.CSSProperties;
    paragraphDefault: React.CSSProperties;
    paragraphSmall: React.CSSProperties;
    buttonLarge: React.CSSProperties;
    buttonDefault: React.CSSProperties;
    buttonSmall: React.CSSProperties;
    sectionTitle: React.CSSProperties;
    subTitle: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    pageTitle?: React.CSSProperties;
    paragraphLeading?: React.CSSProperties;
    paragraphDefault?: React.CSSProperties;
    paragraphSmall?: React.CSSProperties;
    buttonLarge?: React.CSSProperties;
    buttonDefault?: React.CSSProperties;
    buttonSmall?: React.CSSProperties;
    sectionTitle?: React.CSSProperties;
    subTitle?: React.CSSProperties;
  }
}
// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    pageTitle: true;
    sectionTitle: true;
    subTitle: true;
    paragraphLeading: true;
    paragraphDefault: true;
    paragraphSmall: true;
    buttonSmall: true;
    buttonDefault: true;
    buttonLarge: true;
    h5: false;
    h6: false;
    subtitle2: false;
    subtitle1: false;
    body1: false;
    body2: false;
    button: false;
    inherit: false;
  }
}
const fontSize = 14; // px
// Tell Material-UI what's the font-size on the html element.
// 16px is the default font-size used by browsers.
const htmlFontSize = 16;
const coef = fontSize / 14;

export const muiTypographyTheme = {
  MuiTypography: {
    defaultProps: {
      variantMapping: variantMappingConstants,
    },
    pxToRem: (size: any) => `${(size / htmlFontSize) * coef}rem`,
  },

  typography: {
    allVariants: {
      margin: null,
    },
    pxToRem: (size: any) => `${(size / htmlFontSize) * coef}rem`,
    fontFamily: "'InterVariable', sans-serif",
    h5: undefined,
    h6: undefined,
    /* Styles applied to the root element if `variant="h1"`. */
    [variantConstants.H1]: commonProperties(62, 500, 70, "normal"),

    /* Styles applied to the root element if `variant="h2"`. */
    [variantConstants.H2]: commonProperties(42, 500, 56, "normal"),

    /* Styles applied to the root element if `variant="h3"`. */
    [variantConstants.H3]: commonProperties(28, 400, 40, "normal"),

    /* Styles applied to the root element if `variant="h4"`. */
    [variantConstants.H4]: commonProperties(24, 600, 32, "normal"),

    /* Styles applied to the root element if `variant="pageTitle"`. */
    [variantConstants.PAGETITLE]: commonProperties(20, 600, 28, "normal"),

    /* Styles applied to the root element if `variant="sectionTitle"`. */
    [variantConstants.SECTIONTITLE]: commonProperties(18, 600, 26, "0.3px"),

    /* Styles applied to the root element if `variant="subTitle"`. */
    [variantConstants.SUBTITLE]: commonProperties(16, 500, 24, "0.2px"),

    /* Styles applied to the root element if `variant="paragraphLeading"`. */
    [variantConstants.PARAGRAPHLEADING]: commonProperties(18, 400, 28, "0.2px"),

    /* Styles applied to the root element if `variant="paragraphDefault"`. */
    [variantConstants.PARAGRAPHDEFAULT]: commonProperties(16, 400, 24, "normal"),

    /* Styles applied to the root element if `variant="paragraphSmall"`. */
    [variantConstants.PARAGRAPHSMALL]: commonProperties(14, 400, 22, "normal"),

    /* Styles applied to the root element if `variant="buttonSmall"`. */
    [variantConstants.BUTTONSMALL]: commonProperties(14, 500, 22, "normal"),

    /* Styles applied to the root element if `variant="buttonDefault"`. */
    [variantConstants.BUTTONDEFAULT]: commonProperties(16, 500, 24, "normal"),

    /* Styles applied to the root element if `variant="buttonLarge"`. */
    [variantConstants.BUTTONLARGE]: commonProperties(20, 600, 28, "normal"),

    /* Styles applied to the root element if `variant="caption"`. */
    [variantConstants.CAPTION]: commonProperties(12, 400, 20, "0.3px"),

    /* Styles applied to the root element if `variant="captionSmall"`. */
    [variantConstants.CAPTIONSMALL]: commonProperties(10, 400, 16, "0.5px"),

    /* Styles applied to the root element if `variant="overline"`. */
    [variantConstants.OVERLINE]: { ...commonProperties(12, 600, 18, "1.5px"), textTransform: "uppercase" },
  },

  MuiLink: {
    styleOverrides: {
      root: () => {
        return {
          color: "black",
          textDecorationColor: "inherit",
          cursor: "pointer",
        };
      },
    },
  },
};
